import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, ShoppingBag, Sparkles } from "lucide-react";
import { MaskedLine, FadeUp } from "@/components/Reveal";
import EditorialMarquee from "@/components/Marquee";
import VideoCarousel from "@/components/VideoCarousel";
import ProductCard from "@/components/ProductCard";
import { PRODUCTS, HERBALIFE_STORE_URL } from "@/constants/products";

const HERO_IMG = "/shelly-hero.jpeg";

const QUICK_SHOP_IDS = ["liftoff", "protein-shake", "beverage-mix", "collagen"];

const Home = () => {
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 700], [0, 140]);
  const heroScale = useTransform(scrollY, [0, 700], [1.08, 1.2]);

  const quickShop = QUICK_SHOP_IDS.map((id) => PRODUCTS.find((p) => p.id === id));

  return (
    <main data-testid="home-page">
      <section className="grain relative flex min-h-screen items-center overflow-hidden">
        <motion.img
          src={HERO_IMG}
          alt="Shelly mid-jump in black workout gear"
          style={{ y: heroY, scale: heroScale }}
          className="absolute inset-0 h-full w-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-bone/85 via-bone/45 to-bone/5" />
        <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-bone to-transparent" />

        <div className="relative z-10 mx-auto w-full max-w-7xl px-6 pb-16 pt-28">
          <h1 data-testid="home-hero-headline" className="font-display text-4xl font-black uppercase leading-[1.02] tracking-tighter text-ink sm:text-5xl md:text-7xl">
            <MaskedLine delay={0.2}>Get your fav</MaskedLine>
            <MaskedLine delay={0.35}>
              <span className="text-coral">Herbalife</span> products ❤️
            </MaskedLine>
          </h1>
          <FadeUp delay={0.6} className="mt-6 max-w-md">
            <p data-testid="home-hero-subheadline" className="font-body text-base font-light leading-relaxed text-ink/70 md:text-lg">
              My favorite wellness products that I take as part of my daily routine — all in one simple place.
            </p>
          </FadeUp>
          <FadeUp delay={0.75} className="mt-9 flex flex-wrap gap-4">
            <Link
              to="/shop"
              data-testid="hero-shop-button"
              className="group flex items-center gap-2 rounded-full bg-coral px-8 py-4 font-display text-sm font-bold tracking-[0.15em] text-bone shadow-lg shadow-coral/30 transition-all duration-300 hover:scale-105 hover:bg-coraldark"
            >
              SHOP MY FAVORITES
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <Link
              to="/get-started"
              data-testid="hero-get-started-button"
              className="rounded-full border border-forest/30 bg-bone/70 px-8 py-4 font-display text-sm font-bold tracking-[0.15em] text-forest backdrop-blur transition-all duration-300 hover:scale-105 hover:border-coral hover:text-coral"
            >
              GET STARTED
            </Link>
          </FadeUp>
        </div>
      </section>

      <EditorialMarquee />

      <section data-testid="layover-banner" className="border-b border-coral/20 bg-coral/10 px-6 py-6">
        <FadeUp>
          <div className="mx-auto flex max-w-7xl flex-col items-center justify-center gap-4 text-center sm:flex-row sm:gap-6 sm:text-left">
            <img
              src="/layover-bottle.webp"
              alt="LAYOVER luxury laundry detergent bottle"
              className="h-20 w-20 shrink-0 rounded-2xl border border-coral/20 object-cover shadow-md"
            />
            <p className="font-body text-sm font-light text-ink/75 md:text-base">
              <span className="font-display text-sm font-extrabold tracking-[0.1em] text-ink md:text-base">TRY MY NEW LAUNDRY DETERGENT “LAYOVER” — New addictive Luxurious Scent ❤️</span>
            </p>
            <Link
              to="/merch"
              data-testid="layover-banner-button"
              className="group inline-flex shrink-0 items-center gap-2 rounded-full bg-coral px-7 py-3 font-display text-xs font-bold tracking-[0.15em] text-bone shadow-md shadow-coral/30 transition-all duration-300 hover:scale-105 hover:bg-coraldark"
            >
              SHOP LAYOVER ❤️
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </FadeUp>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-24 md:py-32">
        <div className="grid items-center gap-12 md:grid-cols-2">
          <FadeUp>
            <p className="font-display text-xs font-bold tracking-[0.3em] text-coral">MY WELLNESS ROUTINE</p>
            <h2 data-testid="routine-headline" className="mt-4 font-display text-3xl font-black uppercase tracking-tight text-ink md:text-5xl">
              My daily wellness routine
            </h2>
            <p className="mt-6 font-body text-base font-light leading-relaxed text-ink/70 md:text-lg">
              I've created FitbunnyCat.com to make it easy to find the Herbalife products I personally enjoy. Instead of searching everywhere, you can find my favorite products all in one place and shop directly through my Herbalife website.
            </p>
          </FadeUp>
          <FadeUp delay={0.15}>
            <div data-testid="routine-photo" className="overflow-hidden rounded-t-[10rem] rounded-b-3xl border border-forest/10 shadow-xl shadow-forest/10">
              <img
                src="/shelly-hero.jpeg"
                alt="Shelly mid-jump during an outdoor workout"
                className="aspect-[4/5] w-full object-cover object-top transition-transform duration-700 hover:scale-105"
              />
            </div>
          </FadeUp>
        </div>

        <FadeUp className="mt-20">
          <VideoCarousel />
        </FadeUp>
      </section>

      <section className="bg-sage/15 px-6 py-24 md:py-28">
        <div className="mx-auto max-w-7xl">
          <FadeUp className="text-center">
            <p className="font-display text-xs font-bold tracking-[0.3em] text-coral">QUICK SHOP</p>
            <h2 data-testid="quick-shop-headline" className="mt-4 font-display text-3xl font-black uppercase tracking-tight text-ink md:text-5xl">
              My top 4 favorites
            </h2>
          </FadeUp>
          <div className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {quickShop.map((p, i) => (
              <ProductCard key={p.id} product={{ ...p, description: null, button: "SHOP NOW" }} delay={0.08 * i} />
            ))}
          </div>
        </div>
      </section>

      <section className="grain relative overflow-hidden bg-forest px-6 py-24 text-center md:py-32">
        <FadeUp>
          <ShoppingBag className="mx-auto h-10 w-10 text-coral" strokeWidth={1.5} />
          <h2 data-testid="ready-to-shop-headline" className="mt-6 font-display text-4xl font-black uppercase tracking-tight text-bone md:text-6xl">
            Ready to shop?
          </h2>
          <p className="mx-auto mt-4 max-w-md font-body text-base font-light leading-relaxed text-bone/75 md:text-lg">
            Browse my complete Herbalife product selection.
          </p>
          <a
            href={HERBALIFE_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="ready-to-shop-button"
            className="mt-10 inline-flex items-center gap-2 rounded-full bg-coral px-10 py-5 font-display text-sm font-bold tracking-[0.15em] text-bone transition-all duration-300 hover:scale-105 hover:bg-coraldark"
          >
            SHOP MY HERBALIFE WEBSITE
            <ArrowRight className="h-4 w-4" />
          </a>
        </FadeUp>
      </section>
    </main>
  );
};

export default Home;
